import { useEffect, useMemo, useState } from "react";

//** COMPONENT */
import {
  Box,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ModalCustom from "../../../component/modalCustom/modalCustom";
import { notiError } from "../../../component/notification/notifications";

//** API */
import RepairApi, { RepairStatusLogItem } from "../../../api/repair/repair.api";

//** UTILS */
import { colors } from "../../../constants/colors";
import { repairStatusColor, repairStatusLabel } from "../../../constants/repair";
import { dateTimeLogToView } from "../../../utils/date.utils";
import { getApiErrorMessage } from "../../../utils/api-error.utils";
import { stallLabelOf } from "../../../utils/stall.utils";

type Props = {
  repairId: number;
  repairNo?: string;
  zoneName?: string;
  stallName?: string;
  /** optional — หน้าที่ยังไม่ส่งมาจะได้ป้ายเป็นชื่อร้านอย่างเดียวเหมือนเดิม */
  stallNumber?: string;
  onClose: () => void;
};

/** log ที่สถานะเดียวกันและอยู่ติดกัน ถูกยุบเป็นกลุ่มเดียว โชว์ main ก่อน ที่เหลืออยู่ใน children */
type StatusLogGroup = {
  key: string;
  status: string;
  main: RepairStatusLogItem;
  children: RepairStatusLogItem[];
};

export default function ModalStatusLog({ repairId, repairNo, zoneName, stallName, stallNumber, onClose }: Props) {
  const [data, setData] = useState<RepairStatusLogItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  /** กลุ่มที่กางอยู่ — key เป็น logRepairStatusId ของแถวแรกในกลุ่ม */
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  const toggleRow = (key: string) =>
    setExpandedRows((prev) => ({ ...prev, [key]: !prev[key] }));

  useEffect(() => {
    /** กัน setState หลัง unmount ตอนผู้ใช้ปิด modal ก่อน response กลับมา */
    let isMounted = true;

    const loadStatusLog = async () => {
      setIsLoading(true);
      try {
        const res = await RepairApi.statusLog(repairId);
        if (!isMounted) return;
        const rows = Array.isArray(res.data) ? [...res.data] : [];
        // เรียงเก่า -> ใหม่ ให้ timeline อ่านจากบนลงล่าง ไม่พึ่ง order ที่ backend ส่งมา
        rows.sort((a, b) => new Date(a.createDate).getTime() - new Date(b.createDate).getTime());
        setData(rows);
      } catch (error) {
        if (!isMounted) return;
        setData([]);
        notiError(getApiErrorMessage(error, "ดึงประวัติการดำเนินงานไม่สำเร็จ"));
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadStatusLog();
    return () => {
      isMounted = false;
    };
  }, [repairId]);

  /** บรรทัดบอกว่ากำลังดูประวัติของใบไหน — โซน · ร้าน (เลขแผง) · เลขที่เอกสาร */
  const subTitle = [
    zoneName,
    stallLabelOf(stallNumber, stallName, ""),
    repairNo && `เลขที่เอกสาร ${repairNo}`,
  ]
    .filter(Boolean)
    .join(" • ");

  /**
   * ยุบเฉพาะรายการที่สถานะเดียวกัน "และอยู่ติดกัน"
   * ถ้ายุบทุกตัวที่สถานะเดียวกันทั้งลิสต์ ลำดับเวลาของ timeline จะเพี้ยน
   * เช่น รับคำร้อง -> แจ้งเรื่องเพิ่มเติม -> รับคำร้อง จะเหลือ 2 แถวและอ่านผิดความหมาย
   */
  const groups = useMemo(
    () =>
      data.reduce<StatusLogGroup[]>((acc, item) => {
        const last = acc[acc.length - 1];
        if (last && last.status === item.repairStatus) {
          last.children.push(item);
          return acc;
        }
        acc.push({
          key: String(item.logRepairStatusId ?? `${item.repairStatus}-${acc.length}`),
          status: item.repairStatus,
          main: item,
          children: [],
        });
        return acc;
      }, []),
    [data]
  );

  const ROW_GRID = { xs: "1fr", md: "1.2fr 1.6fr 1.4fr auto" };
  const HEAD_CELLS = ["วันที่/เวลาดำเนินการ", "สถานะ/รายละเอียด", "ผู้ดำเนินการ"];

  /** ทุกรายการมี remark ของตัวเอง (ตอนนี้บาง record ยังเป็น null รอ backend แก้) */
  const renderRemark = (remark?: string | null) => <Typography
    sx={{
      fontSize: 14,
      color: colors.textLightGray,
      whiteSpace: "pre-line",
      wordBreak: "break-word",
    }}
  >
    หมายเหตุ : {remark || ""}
  </Typography>


  const renderGroup = (group: StatusLogGroup, index: number) => {
    const { key, main, children } = group;
    const isExpanded = !!expandedRows[key];
    /** ปุ่มกางคุมเฉพาะรายการที่สถานะซ้ำกัน — หมายเหตุของแถวหลักโชว์อยู่แล้ว ไม่ได้ซ่อน */
    const canExpand = children.length > 0;

    return (
      <Box key={key}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: ROW_GRID,
            alignItems: "flex-start",
            gap: 1,
            py: 1.5,
          }}
        >
          <Typography sx={{ fontSize: 16, color: colors.textLightGray }}>
            {main.createDate ? dateTimeLogToView(main.createDate) : "-"}
          </Typography>
          <Box minWidth={0}>
            <Typography
              sx={{ fontSize: 16, fontWeight: 600, color: repairStatusColor(main.repairStatus) }}
            >
              {repairStatusLabel(main.repairStatus)}
              {children.length > 0 && (
                <Typography component="span" sx={{ fontSize: 14, color: colors.textLightGray }}>
                  {` (${children.length + 1})`}
                </Typography>
              )}
            </Typography>
            {/* หมายเหตุของแถวหลักโชว์ใต้สถานะตลอด ไม่อยู่ในส่วนที่ต้องกด + ถึงจะเห็น */}
            {renderRemark(main.remark)}
          </Box>
          <Typography sx={{ fontSize: 16, fontWeight: 600 }}>{main.createName || "-"}</Typography>
          <Box sx={{ minWidth: 40 }}>
            {canExpand && (
              <Tooltip title={isExpanded ? "ซ่อนรายละเอียด" : "ดูรายละเอียด"}>
                <IconButton
                  size="small"
                  onClick={() => toggleRow(key)}
                  sx={{ color: colors.themeMainColor }}
                >
                  {isExpanded ? <RemoveIcon fontSize="small" /> : <AddIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* unmountOnExit เพื่อไม่ให้เนื้อหาที่ยังไม่ได้กางค้างอยู่ใน DOM */}
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Box sx={{ pb: 1.5 }}>
            {children.map((child, childIndex) => (
              <Box
                key={child.logRepairStatusId ?? `${key}-${childIndex}`}
                sx={{ display: "grid", gridTemplateColumns: ROW_GRID, gap: 1, mt: 1 }}
              >
                <Typography sx={{ fontSize: 14, color: colors.textLightGray, pl: 2 }}>
                  {`- ${child.createDate ? dateTimeLogToView(child.createDate) : "-"}`}
                </Typography>
                {/* Box ต้อง render เสมอแม้ remark ว่าง ไม่งั้นชื่อผู้ทำจะเลื่อนไปกินคอลัมน์นี้ */}
                <Box minWidth={0}>{renderRemark(child.remark)}</Box>
                <Typography sx={{ fontSize: 14, color: colors.textLightGray }}>
                  {child.createName || "-"}
                </Typography>
              </Box>
            ))}
          </Box>
        </Collapse>

        {index !== groups.length - 1 && <Divider />}
      </Box>
    );
  };

  const renderBody = () => (
    <Box sx={{ px: { xs: 2, md: 3 }, pb: 1 }}>
      {subTitle && (
        <Typography
          sx={{ fontSize: 16, color: colors.textLightGray, textAlign: "center", mb: 2 }}
        >
          {subTitle}
        </Typography>
      )}

      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ py: 4 }}>
          <CircularProgress size={32} sx={{ color: colors.themeMainColor }} />
        </Box>
      ) : groups.length === 0 ? (
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ py: 4 }}>
          <Typography sx={{ color: colors.textLightGray }}>ไม่พบประวัติการดำเนินงาน</Typography>
        </Box>
      ) : (
        <>
          {/* หัวคอลัมน์อยู่นอกกล่อง scroll จะได้ค้างให้เห็นตลอดตอนเลื่อนดูรายการ
              ซ่อนบนจอเล็กเพราะ ROW_GRID ยุบเหลือคอลัมน์เดียว หัวตารางจะไม่ตรงกับข้อมูล */}
          <Box
            sx={{
              display: { xs: "none", md: "grid" },
              gridTemplateColumns: ROW_GRID,
              gap: 1,
              pb: 1,
              borderBottom: `1px solid ${colors.lightGray}`,
            }}
          >
            {HEAD_CELLS.map((label) => (
              <Typography
                key={label}
                sx={{ fontSize: 14, fontWeight: 600, color: colors.textLightGray }}
              >
                {label}
              </Typography>
            ))}
            <Box sx={{ minWidth: 40 }} />
          </Box>

          <Box className="custom-scroll" sx={{ maxHeight: 400, overflowY: "auto" }}>
            {groups.map(renderGroup)}
          </Box>
        </>
      )}
    </Box>
  );

  return (
    <ModalCustom
      // ต้องเป็น true เสมอ + ให้ parent conditional render (ค่า false จะ fall back ไปอ่าน redux)
      isShowProps
      // ปิด modal เองผ่าน onClose ไม่ให้ไป reset redux modal.isShow ของ modal อื่นในหน้าเดียวกัน
      autoResetModal={false}
      size="lg"
      closeButton
      alignFooter="center"
      title="ประวัติการดำเนินงาน"
      component={renderBody()}
      // ไม่ส่ง onSubmit/textBtnConfirm ปุ่มยืนยันจึงไม่ render เหลือแค่ปุ่มปิด
      textBtnCancel="ปิด"
      onReset={onClose}
      onClose={onClose}
    />
  );
}
